import type { NarrativeBlock } from "@hamlak/shared";
import type { BlockContent, DefinitionContent, PhrasingContent } from "mdast";
import type { ReactNode } from "react";
import styles from "./CharacterMessage.module.css";
import bubble from "./MyBubble.module.css";
import { cx } from "../cx";

/**
 * parseNarrative 블록을 그린다. 서식 규약 정본 docs/narrative-format.md (AC0-254).
 * 장면 `p[data-kind=scene]`(말풍선 없이 이탤릭·subtle) · 지문 `p[data-kind=narration]`(회색, 기울임 없음) · 대사 `p[data-kind=dialogue]`(말풍선) ·
 * 속마음 `p[data-kind=thought]`(작은 말풍선, AC0-244) · 제목 `h1~h3[data-kind=heading]` · 코드 `pre[data-kind=code]` · 목록 `ul|ol[data-kind=list]` · 구분선 `hr[data-kind=rule]`.
 * mine 이면 내 메시지: 전부 오른쪽 정렬, 대사는 내 말풍선(MyBubble 모양, brand).
 */
export function NarrativeBlocks({
  blocks,
  streaming,
  plain,
  mine,
}: {
  blocks: NarrativeBlock[];
  streaming?: boolean | undefined;
  /** 본문 모드: 기호 없는 문단을 말풍선 대신 일반 문단(`p[data-kind=text]`)으로. 소개·첫 장면 text 블록용 */
  plain?: boolean | undefined;
  /** 내 메시지(오른쪽). 대사는 MyBubble 말풍선 */
  mine?: boolean | undefined;
}) {
  const last = blocks.length - 1;
  return (
    <>
      {blocks.map((b, i) => {
        const cursor = streaming && i === last ? <span className={styles.cursor} aria-hidden /> : null;
        if (b.kind !== "block") {
          const kind = plain && b.kind === "dialogue" ? "text" : b.kind;
          const className = mine ? cx(kind === "dialogue" ? bubble.bubble : styles[kind], styles.mine) : styles[kind];
          return (
            <p key={i} className={className} data-kind={kind}>
              {inline(b.children)}
              {cursor}
            </p>
          );
        }
        return <Block key={i} node={b.node} cursor={cursor} />;
      })}
    </>
  );
}

const HEADING = ["h1", "h2", "h3"] as const;

function Block({ node, cursor }: { node: Extract<NarrativeBlock, { kind: "block" }>["node"]; cursor: ReactNode }) {
  switch (node.type) {
    case "heading": {
      const Tag = HEADING[Math.min(node.depth, 3) - 1]!;
      return (
        <Tag className={styles[Tag]} data-kind="heading">
          {inline(node.children)}
          {cursor}
        </Tag>
      );
    }
    case "code":
      return (
        <pre className={styles.code} data-kind="code">
          <code>{node.value}</code>
          {cursor}
        </pre>
      );
    case "list": {
      const Tag = node.ordered ? "ol" : "ul";
      return (
        <Tag className={styles.list} data-kind="list" {...(node.ordered && node.start != null ? { start: node.start } : {})}>
          {node.children.map((item, i) => (
            <li key={i}>
              {nested(item.children)}
              {i === node.children.length - 1 && cursor}
            </li>
          ))}
        </Tag>
      );
    }
    case "thematicBreak":
      return <hr className={styles.rule} data-kind="rule" />;
  }
}

/** 목록 안의 블록: 문단은 여백 없는 블록으로, 목록은 순서 유지, 인용은 장면 모양. 그릴 수 없는 노드는 shared 가 이미 원문 문단으로 바꿔 놓았다 */
function nested(children: (BlockContent | DefinitionContent)[]): ReactNode[] {
  return children.map((c, i) => {
    switch (c.type) {
      case "paragraph":
        return (
          <p key={i} className={styles.nestedP}>
            {inline(c.children)}
          </p>
        );
      case "heading":
        return (
          <p key={i} className={styles.nestedP}>
            <strong className={styles.strong}>{inline(c.children)}</strong>
          </p>
        );
      case "code":
        return (
          <pre key={i} className={styles.code}>
            <code>{c.value}</code>
          </pre>
        );
      case "list": {
        const Tag = c.ordered ? "ol" : "ul";
        return (
          <Tag key={i} className={styles.list} {...(c.ordered && c.start != null ? { start: c.start } : {})}>
            {c.children.map((item, j) => (
              <li key={j}>{nested(item.children)}</li>
            ))}
          </Tag>
        );
      }
      case "blockquote":
        return (
          <blockquote key={i} className={styles.scene}>
            {nested(c.children)}
          </blockquote>
        );
      case "thematicBreak":
        return <hr key={i} className={styles.rule} />;
      default:
        return null;
    }
  });
}

function inline(nodes: PhrasingContent[]): ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.type) {
      case "text":
        return n.value;
      case "emphasis":
        return (
          <span key={i} className={styles.em}>
            {inline(n.children)}
          </span>
        );
      case "strong":
        return (
          <strong key={i} className={styles.strong}>
            {inline(n.children)}
          </strong>
        );
      case "delete":
        return (
          <del key={i} className={styles.del}>
            {inline(n.children)}
          </del>
        );
      case "inlineCode":
        return (
          <code key={i} className={styles.inlineCode}>
            {n.value}
          </code>
        );
      case "break":
        return <br key={i} />;
      default:
        return null;
    }
  });
}
